import React, { useState, useEffect, useMemo, useRef } from "react";
import "./summarypage.css";
import restart from "../assets/restart.png";
import { FaArrowLeft, FaDownload, FaShareAlt } from "react-icons/fa";
import { faStar } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { paginateCanvasToPdf } from "./pdfBreakHelper";
import { apiUrl } from "../apiConfig";

const API_BASE = `${apiUrl("therapist_api")}/`;

const fetchWithTimeout = async (url, options = {}, timeout = 10000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
};

export default function Summarypage() {
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("token");

  const [sessionId, setSessionId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFavourite, setIsFavourite] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  // -- Review Smart Goal handoff context -------------------------------------
  // Needed so "Save and View Intervention" opens /Interventions in smart-goal
  // mode with the goal session to revise. This used to be read from
  // location.search ONLY, and the summary page is reached by a bare
  // navigate("/summarypage") from several places, so the context was routinely
  // absent and the revision silently degraded into "create a new goal".
  //
  // Resolution order: explicit URL param -> localStorage assessmentState (kept
  // in sync by start-assessment.js) -> the appointment type the summary payload
  // reports for this session. The last source means the mode is still recovered
  // when both the URL and the stored state are missing.
  const goalContext = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const fromUrl = params.get("smartGoal") === "1";
    const urlGoalSessionId = params.get("goalSessionId") || params.get("goalSession") || null;

    let stored = null;
    try {
      const raw = localStorage.getItem("assessmentState");
      if (raw) stored = JSON.parse(raw);
    } catch (e) { /* corrupt state must not break the summary page */ }

    // The persisted `session` is the appointment label, e.g. "Review Smart Goal".
    const fromStore = String(stored?.session || "").trim().toLowerCase() === "review smart goal";
    const storeGoalSessionId = stored?.goalSessionId ? String(stored.goalSessionId) : null;

    // Last resort: whatever get_test_intervention.php reports for this session.
    const aptType = String(data?.session?.appointment_type || data?.appointment_type || "").trim().toLowerCase();
    const fromData = aptType === "review smart goal";

    const smartGoal = fromUrl || fromStore || fromData;
    const goalSessionId = urlGoalSessionId || storeGoalSessionId || null;
    return { smartGoal, goalSessionId };
  }, [location.search, data]);

  const summaryRef = useRef(null);

  // -- 1. Resolve sessionId (URL param first, then localStorage) ------------
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const urlSessionId = urlParams.get("sessionId");
    if (urlSessionId) {
      setSessionId(urlSessionId);
      return;
    }
    const savedState = localStorage.getItem("assessmentState");
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        if (parsed.currentSessionId) setSessionId(parsed.currentSessionId);
      } catch (e) {
        console.error("Error parsing saved state", e);
      }
    }
  }, [location.search]);

  // -- 2. Fetch intervention / medical-history summary data ------------------
  useEffect(() => {
    if (!sessionId || !token) return;
    setLoading(true);
    setError(null);

    const fetchSummary = async () => {
      try {
        const response = await fetchWithTimeout(
          `${API_BASE}get_test_intervention.php`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              session_id: parseInt(sessionId),
              // Review Smart Goal: scope goal-domain inheritance to the session
              // that owns the goal being reviewed.
              ...(goalContext.goalSessionId ? { goal_session_id: parseInt(goalContext.goalSessionId) } : {}),
            }),
          }
        );
        const json = await response.json();
        if (json.status && json.session) {
          setData(json);
        } else {
          setError(json.message || "Failed to load summary");
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [sessionId, token]);

  // -- 3. Fetch favourite status ---------------------------------------------
  useEffect(() => {
    if (!sessionId || !token) return;
    const fetchFavourite = async () => {
      try {
        const response = await fetchWithTimeout(
          `${API_BASE}pathway_favorites.php`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ sessionId: parseInt(sessionId), action: "status" }),
          }
        );
        const json = await response.json();
        if (json.status && typeof json.is_favourite !== "undefined") {
          setIsFavourite(Boolean(json.is_favourite));
        }
      } catch (e) {
        console.error("Failed to fetch favourite status:", e.message);
      }
    };
    fetchFavourite();
  }, [sessionId, token]);

  // -- 4. Favourite toggle ---------------------------------------------------
  const handleToggleFavourite = async () => {
    if (!sessionId || !token) return;
    try {
      const response = await fetchWithTimeout(
        `${API_BASE}pathway_favorites.php`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ sessionId: parseInt(sessionId), action: "toggle" }),
        }
      );
      const json = await response.json();
      if (json.status && typeof json.is_favourite !== "undefined") {
        setIsFavourite(Boolean(json.is_favourite));
      }
    } catch (e) {
      console.error("Failed to toggle favourite:", e.message);
    }
  };

  const handleRestart = () => {
    localStorage.removeItem("assessmentState");
    navigate("/start-assessment");
  };

  const session = data?.session;
  const clientName = session?.client_name || "Client";

  // -- 5. Derived helpers ----------------------------------------------------
  const formatList = (arr) => {
    if (!arr) return "N/A";
    const list = Array.isArray(arr) ? arr : [arr];
    const cleaned = list
      .map((i) => (typeof i === "string" ? i.trim() : ""))
      .filter(Boolean);
    return cleaned.length > 0 ? cleaned.join(", ") : "N/A";
  };

  // Group intervention data by goal domain (from data keyed by goal tab).
  const interventionsByDomain = useMemo(() => {
    const grouped = {};
    if (!data?.data) return grouped;
    Object.values(data.data).forEach((list) => {
      (list || []).forEach((item) => {
        const domain = (item.goal_domain || "General").trim();
        const title = item.intervention_title || item.intervention_section_title || "";
        if (!title) return;
        if (!grouped[domain]) grouped[domain] = [];
        if (!grouped[domain].includes(title)) grouped[domain].push(title);
      });
    });
    return grouped;
  }, [data]);

  // -- 6. PDF generation (Download Summary) ----------------------------------
  const loadScript = (src) => {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) return resolve();
      const script = document.createElement("script");
      script.src = src;
      script.onload = resolve;
      script.onerror = reject;
      document.body.appendChild(script);
    });
  };

  const handleDownload = async () => {
    if (!summaryRef.current) return;
    setIsGeneratingPdf(true);
    try {
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js");
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
      if (!window.html2canvas || !window.jspdf) throw new Error("PDF libraries unavailable");
      await new Promise((r) => setTimeout(r, 800));

      const element = summaryRef.current;
      const canvas = await window.html2canvas(element, {
        scale: 1.5,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        scrollX: 0,
        scrollY: -window.scrollY,
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight,
      });

      const { jsPDF } = window.jspdf;
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = 210;
      const pageHeightMm = pdf.internal.pageSize.getHeight();

      const pages = paginateCanvasToPdf({ canvas, element, pdfWidth, pageHeightMm });
      pages.forEach((p, i) => {
        if (i > 0) pdf.addPage();
        pdf.addImage(p.sliceImgData, "JPEG", 0, 0, pdfWidth, p.sliceHeightMm);
      });

      const safeName = clientName.replace(/[^a-zA-Z0-9]+/g, "_");
      pdf.save(`Summary_${safeName}.pdf`);
    } catch (err) {
      console.error("PDF generation failed:", err);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleShare = async () => {
    if (navigator.share && data) {
      try {
        await navigator.share({
          title: `Summary of ${clientName}`,
          text: `Assessment summary for ${clientName}`,
        });
        return;
      } catch (e) {
        if (e.name === "AbortError") return;
      }
    }
    setShowShareModal(true);
  };

  // -- 7. Render -------------------------------------------------------------
  return (
    <>
      <div className="summary-container">
        {/* === Header Bar === */}
        <div className="summary-header-bar">
          <Link to="/dashboard" className="back-btn3" aria-label="Go to previous page">
              <FaArrowLeft className="back-icon3" aria-hidden="true" />
            <span>Previous page</span>
          </Link>

          <div className="header-titles">
            <h1>Summary of {clientName}</h1>
          </div>

          <div className="header-actions1">
            {session?.client_id && (
              <button
                className="header-btn1"
                onClick={() => navigate(`/historical-pathway?client_id=${session.client_id}`)}
                aria-label="View History"
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <h4 aria-hidden="true">View History</h4>
              </button>
            )}

            <button className="header-btn1 restart-btn1" onClick={handleRestart} aria-label="Restart Assessment">
              <img src={restart} alt="" aria-hidden="true" className="header-icon11" />
              <h4 aria-hidden="true">Restart Assessment</h4>
            </button>

            <button
              className={`header-btn1 fav-btn1 ${isFavourite ? "active" : ""}`}
              onClick={handleToggleFavourite}
              aria-label={isFavourite ? "Remove from favourites" : "Add to favourites"}
              aria-pressed={isFavourite}
            >
              <FontAwesomeIcon icon={faStar} className="star-icon11" aria-hidden="true" />
              <h4 aria-hidden="true">Favourite</h4>
            </button>
          </div>
        </div>

        {/* === Content Wrapper === */}
        <div className="sum-cont" ref={summaryRef}>
          {/* === Intro Section === */}
          <div className="summary-intro">
            <p>
              This page provides an overview of pathway information, including
              history, treatment goals, and session summaries.
            </p>
          </div>

          {loading && <div className="summary-loading" role="status" aria-live="polite">Loading summary...</div>}
          {error && <div className="summary-loading" role="alert">Failed to load summary: {error}</div>}

          {!loading && !error && data && (
            <>
              {/* === Subheading === */}
              <div className="summary-subtitle">
                <h3>Interpreting medical history and combined assessment findings</h3>
                <ul className="medical-list">
                  <li>
                    <span className="icon-circle"></span>
                    <span>Age: {session?.age_group || "N/A"}</span>
                  </li>
                  <li>
                    <span className="icon-circle"></span>
                    <span>Topography: {session?.topography ? formatList(session.topography) : "N/A"}</span>
                  </li>
                  <li>
                    <span className="icon-circle"></span>
                    <span>Severity: {session?.severity ? formatList(session.severity) : "N/A"}</span>
                  </li>
                  <li>
                    <span className="icon-circle"></span>
                    <span>
                      Early indicators of comorbidities:{" "}
                      {session?.comorbidities && session.comorbidities.length
                        ? formatList(session.comorbidities)
                        : "N/A"}
                    </span>
                  </li>
                  <li>
                    <span className="icon-circle"></span>
                    <span>
                      Goal Domains identified:{" "}
                      {session?.goal_domains && session.goal_domains.length
                        ? formatList(session.goal_domains)
                        : "N/A"}
                    </span>
                  </li>
                </ul>
              </div>

              {/* === Goal Domain Limiting Factors === */}
              {session?.goal_limiting_factors && session.goal_limiting_factors.length > 0 && (
                <div className="goal-section">
                  <div className="goal-header">Goal domain limiting factors</div>
                  <div className="goal-block">
                    <ul>
                      {session.goal_limiting_factors.map((factor, idx) => (
                        <li key={idx}>{factor}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* === Goal Domain Sections === */}
              {Object.keys(interventionsByDomain).length > 0 ? (
                Object.entries(interventionsByDomain).map(([domain, titles], idx) => (
                  <div className="goal-section" key={idx}>
                    <div className="goal-header">{domain}</div>
                    <div className="goal-block">
                      <h3>Recommended Interventions</h3>
                      <div className="tag-container">
                        {titles.map((title, i) => (
                          <button className="tag-btn" key={i}>{title}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="goal-section">
                  <div className="goal-header">Recommended Interventions</div>
                  <div className="goal-block">
                    <p>No recommended interventions available for this session.</p>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Spacer for fixed footer */}
          <div className="footer-spacer"></div>
        </div>

        {/* === Footer Buttons (Fixed) === */}
        <div className="summary-footer">
          {sessionId && (
            <button
              className="secondary-btn"
              onClick={() => {
                const params = new URLSearchParams({ sessionId: String(sessionId) });
                // Review Smart Goal: carry the mode + the goal session so
                // Interventions knows which existing goal to revise.
                if (goalContext.smartGoal) {
                  params.set("smartGoal", "1");
                  if (goalContext.goalSessionId) params.set("goalSessionId", goalContext.goalSessionId);
                  params.set("fromReview", "1");
                }
                navigate(`/Interventions?${params.toString()}`);
              }}
            >
              Save and View Intervention
            </button>
          )}
          {sessionId && (
            <button
              className="secondary-btn"
              onClick={() => navigate(`/updategoal?sessionId=${sessionId}`)}
            >
              Set Goal Procedure
            </button>
          )}
          <button className="primary-btn" onClick={handleDownload} disabled={isGeneratingPdf}>
            {isGeneratingPdf ? "Generating?" : "Download Summary"}{" "}
            {!isGeneratingPdf && <FaDownload className="btn-icon" aria-hidden="true" />}
          </button>
          <button className="secondary-btn" onClick={handleShare}>
            Share <FaShareAlt className="btn-icon" aria-hidden="true" />
          </button>
        </div>

        {/* Share Modal */}
        {showShareModal && (
          <div
            className="share-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-label="Share summary"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(0,0,0,0.5)",
              backdropFilter: "blur(5px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1050,
            }}
            onClick={() => setShowShareModal(false)}
          >
            <div
              className="share-modal"
              style={{
                background: "#fff",
                padding: "24px",
                borderRadius: "8px",
                maxWidth: "400px",
                width: "90%",
                textAlign: "center",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h5 style={{ marginBottom: "12px" }}>Share Summary</h5>
              <p style={{ marginBottom: "16px", color: "#555" }}>
                Copy the link below to share this assessment summary.
              </p>
              <input
                type="text"
                readOnly
                value={`${window.location.origin}${window.location.pathname}?sessionId=${sessionId}`}
                aria-label="Shareable summary link"
                style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid var(--cpa-gray-3, #737A7E)", marginBottom: "12px" }}
              />
              <button
                className="primary-btn"
                style={{ width: "100%" }}
                onClick={() => {
                  navigator.clipboard?.writeText(`${window.location.origin}${window.location.pathname}?sessionId=${sessionId}`);
                  setShowShareModal(false);
                }}
              >
                Copy Link
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

